import React, { useState, useEffect, useRef, useLayoutEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import axios from '../../api';
import useLatestOrgId from './useLatestOrgId';

const CONNECTOR_TYPES = ['AWS', 'GITHUB', 'AZURE_AD', 'OKTA', 'SSH'];

const CONNECTOR_ICONS = {
  AWS:      'fa-aws',
  GITHUB:   'fa-github',
  AZURE_AD: 'fa-microsoft',
  OKTA:     'fa-shield-alt',
  SSH:      'fa-terminal',
};

export default function ConnectorsTab({ onSyncComplete, orgId }) {
  const { t } = useTranslation();
  const latestOrgIdRef = useLatestOrgId(orgId);

  const CONNECTOR_FIELDS = {
    AWS: [
      { key: 'accessKeyId',     label: t('ConnectorsTab.fieldAccessKeyId'),     type: 'text',     placeholder: 'AKIA...' },
      { key: 'secretAccessKey', label: t('ConnectorsTab.fieldSecretAccessKey'), type: 'password', placeholder: '•••' },
    ],
    GITHUB: [
      { key: 'token', label: t('ConnectorsTab.fieldPersonalAccessToken'), type: 'password', placeholder: 'ghp_...' },
      { key: 'org',   label: t('ConnectorsTab.fieldGithubOrg'), type: 'text',     placeholder: 'my-org' },
    ],
    AZURE_AD: [
      { key: 'tenantId',     label: t('ConnectorsTab.fieldTenantId'),     type: 'text',     placeholder: 'xxxxxxxx-xxxx-...' },
      { key: 'clientId',     label: t('ConnectorsTab.fieldClientId'),     type: 'text',     placeholder: 'xxxxxxxx-xxxx-...' },
      { key: 'clientSecret', label: t('ConnectorsTab.fieldClientSecret'), type: 'password', placeholder: '•••' },
    ],
    OKTA: [
      { key: 'domain',   label: t('ConnectorsTab.fieldOktaDomain'),   type: 'text',     placeholder: 'company.okta.com' },
      { key: 'apiToken', label: t('ConnectorsTab.fieldApiToken'),      type: 'password', placeholder: '•••' },
    ],
    SSH: [
      { key: 'host',       label: t('ConnectorsTab.fieldHostIp'),       type: 'text',     placeholder: '10.0.1.20 or bastion.example.com' },
      { key: 'port',       label: t('ConnectorsTab.fieldPort'),            type: 'text',     placeholder: '22' },
      { key: 'username',   label: t('ConnectorsTab.fieldSshUsername'),    type: 'text',     placeholder: 'ec2-user' },
      { key: 'password',   label: t('ConnectorsTab.fieldPassword'),          type: 'password',  placeholder: t('ConnectorsTab.placeholderOrUsePrivateKey') },
      { key: 'privateKey', label: t('ConnectorsTab.fieldPrivateKeyPem'), type: 'textarea', placeholder: '-----BEGIN RSA PRIVATE KEY-----\n...\n-----END RSA PRIVATE KEY-----' },
    ],
  };

  const [connectors, setConnectors] = useState([]);
  const [connectorsLoaded, setConnectorsLoaded] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ connectorType: 'AWS', displayName: '', region: '', credentials: {} });
  const [saving, setSaving] = useState(false);
  const [testingIds, setTestingIds] = useState(() => new Set());
  const [syncJobs, setSyncJobs] = useState({});   // connectorId → { jobId, status, result }
  const [testResults, setTestResults] = useState({});
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [editError, setEditError] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const pollRefs = useRef({});
  const editingIdRef = useRef(editingId);
  useLayoutEffect(() => { editingIdRef.current = editingId; }, [editingId]);

  const load = useCallback(() => {
    const params = orgId ? { orgId } : {};
    setConnectorsLoaded(false);
    axios.get('/iasm/connectors', { params }).then(r => {
      if (latestOrgIdRef.current !== orgId) return; // org switched while this request was in flight
      setConnectors(r.data);
    }).catch(() => {})
      .finally(() => { if (latestOrgIdRef.current === orgId) setConnectorsLoaded(true); });
  }, [orgId, latestOrgIdRef]);

  useEffect(() => { load(); }, [load]);

  // Abandon any in-flight sync polls from the previous org the instant the org changes —
  // otherwise a poll tick landing after the switch can write a completed/failed sync
  // status into syncJobs under a connectorId that may collide with the new org's own.
  useEffect(() => {
    return () => {
      Object.values(pollRefs.current).forEach(clearInterval);
      pollRefs.current = {};
      setSyncJobs({});
    };
  }, [orgId]);

  const save = () => {
    setSaving(true);
    setSaveError(null);
    const filteredCreds = Object.fromEntries(
      Object.entries(form.credentials || {}).filter(([, v]) => v && v.toString().trim())
    );
    const payload = { ...form, credentials: filteredCreds };
    const params = orgId ? { orgId } : {};
    axios.post('/iasm/connectors', payload, { params })
      .then(() => {
        setShowForm(false);
        setSaveError(null);
        setForm({ connectorType: 'AWS', displayName: '', region: '', credentials: {} });
        load();
      })
      .catch(err => setSaveError(err?.response?.data?.message || err?.message || t('ConnectorsTab.saveFailed')))
      .finally(() => setSaving(false));
  };

  const deleteConnector = (id) => {
    if (!window.confirm(t('ConnectorsTab.confirmDelete'))) return;
    const params = orgId ? { orgId } : {};
    axios.delete(`/iasm/connectors/${id}`, { params }).then(load).catch(() => {});
  };

  const testConn = (id) => {
    setTestingIds(prev => new Set(prev).add(id));
    setTestResults(prev => ({ ...prev, [id]: null }));
    const params = orgId ? { orgId } : {};
    const testOrgId = orgId;
    axios.post(`/iasm/connectors/${id}/test`, null, { params })
      .then(r => {
        if (latestOrgIdRef.current !== testOrgId) return; // org switched while this request was in flight
        setTestResults(prev => ({ ...prev, [id]: r.data }));
      })
      .catch(() => {
        if (latestOrgIdRef.current !== testOrgId) return;
        setTestResults(prev => ({ ...prev, [id]: { success: false, message: t('ConnectorsTab.connectionFailed') } }));
      })
      .finally(() => setTestingIds(prev => { // always clear this id's spinner, even for a stale org -- UI-only flag, no data to leak
        const next = new Set(prev);
        next.delete(id);
        return next;
      }));
  };

  const pollJob = useCallback((connectorId, jobId, pollOrgId) => {
    if (pollRefs.current[connectorId]) clearInterval(pollRefs.current[connectorId]);
    pollRefs.current[connectorId] = setInterval(async () => {
      try {
        const r = await axios.get(`/iasm/scans/${jobId}`);
        if (latestOrgIdRef.current !== pollOrgId) { clearInterval(pollRefs.current[connectorId]); return; } // org switched mid-poll
        const job = r.data;
        setSyncJobs(prev => ({ ...prev, [connectorId]: { jobId, status: job.status, result: job } }));
        if (job.status === 'COMPLETED' || job.status === 'FAILED') {
          clearInterval(pollRefs.current[connectorId]);
          load();
          onSyncComplete && onSyncComplete();
        }
      } catch {
        clearInterval(pollRefs.current[connectorId]);
      }
    }, 3000);
  }, [load, onSyncComplete, latestOrgIdRef]);

  const syncConn = (id) => {
    setSyncJobs(prev => ({ ...prev, [id]: { status: 'PENDING' } }));
    const params = orgId ? { orgId } : {};
    axios.post(`/iasm/connectors/${id}/sync`, null, { params })
      .then(r => {
        if (latestOrgIdRef.current !== orgId) return; // org switched while sync-start request was in flight
        const jobId = r.data.jobId;
        setSyncJobs(prev => ({ ...prev, [id]: { jobId, status: 'RUNNING' } }));
        pollJob(id, jobId, orgId);
      })
      .catch(err => {
        const status = err?.response?.status;
        const msg = status === 402
          ? (err?.response?.data?.message || t('ConnectorsTab.insufficientCredits')) + t('ConnectorsTab.visitCreditsTab')
          : (err?.response?.data?.message || t('ConnectorsTab.failedToStartSync'));
        setSyncJobs(prev => ({ ...prev, [id]: { status: 'FAILED', result: { errorMessage: msg } } }));
      });
  };

  const saveEdit = (id) => {
    setSavingEdit(true);
    setEditError(null);
    const filteredCreds = Object.fromEntries(
      Object.entries(editForm.credentials || {}).filter(([, v]) => v && v.trim())
    );
    const payload = { ...editForm, credentials: filteredCreds };
    const params = orgId ? { orgId } : {};
    axios.put(`/iasm/connectors/${id}`, payload, { params })
      .then(() => {
        load();
        if (editingIdRef.current !== id) return; // user switched to editing a different connector while this save was in flight
        setEditingId(null);
        setEditError(null);
      })
      .catch(err => {
        if (editingIdRef.current !== id) return;
        setEditError(err?.response?.data?.message || err?.message || t('ConnectorsTab.saveFailed'));
      })
      .finally(() => setSavingEdit(false)); // always clear the spinner, even for a stale edit target -- it's a UI-only flag, no data to leak
  };

  const setCredField = (key, value) => {
    setForm(f => ({ ...f, credentials: { ...f.credentials, [key]: value } }));
  };

  const fields = CONNECTOR_FIELDS[form.connectorType] || [];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '16px' }}>
        <button onClick={() => setShowForm(!showForm)} style={{
          padding: '8px 18px', background: '#2563eb', color: '#fff', border: 'none',
          borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontWeight: 600,
        }}>
          <i className="fas fa-plus" style={{ marginRight: '6px' }} />
          {showForm ? t('ConnectorsTab.cancel') : t('ConnectorsTab.addConnector')}
        </button>
      </div>

      {showForm && (
        <div style={{
          background: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px',
          padding: '20px', marginBottom: '20px',
        }}>
          <h4 style={{ margin: '0 0 16px', fontSize: '15px', fontWeight: 600 }}>{t('ConnectorsTab.newConnector')}</h4>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
            <div>
              <label style={{ fontSize: '12px', fontWeight: 500, color: '#374151' }}>{t('ConnectorsTab.connectorType')}</label>
              <select value={form.connectorType}
                onChange={e => setForm(f => ({ ...f, connectorType: e.target.value, credentials: {} }))}
                style={{ display: 'block', width: '100%', marginTop: '4px', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px' }}>
                {CONNECTOR_TYPES.map(t => <option key={t} value={t}>{t.replace('_', ' ')}</option>)}
              </select>
            </div>
            <div>
              <label style={{ fontSize: '12px', fontWeight: 500, color: '#374151' }}>{t('ConnectorsTab.displayName')}</label>
              <input value={form.displayName} onChange={e => setForm(f => ({ ...f, displayName: e.target.value }))}
                placeholder={t('ConnectorsTab.displayNamePlaceholder')}
                style={{ display: 'block', width: '100%', marginTop: '4px', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px', boxSizing: 'border-box' }} />
            </div>
            {(form.connectorType === 'AWS') && (
              <div>
                <label style={{ fontSize: '12px', fontWeight: 500, color: '#374151' }}>{t('ConnectorsTab.region')}</label>
                <input value={form.region} onChange={e => setForm(f => ({ ...f, region: e.target.value }))}
                  placeholder="us-east-1"
                  style={{ display: 'block', width: '100%', marginTop: '4px', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px', boxSizing: 'border-box' }} />
              </div>
            )}
            {fields.map(field => (
              <div key={field.key} style={field.type === 'textarea' ? { gridColumn: '1 / -1' } : {}}>
                <label style={{ fontSize: '12px', fontWeight: 500, color: '#374151' }}>{field.label}</label>
                {field.type === 'textarea' ? (
                  <textarea
                    value={form.credentials[field.key] || ''}
                    onChange={e => setCredField(field.key, e.target.value)}
                    placeholder={field.placeholder}
                    rows={6}
                    style={{ display: 'block', width: '100%', marginTop: '4px', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '12px', fontFamily: 'monospace', boxSizing: 'border-box', resize: 'vertical' }}
                  />
                ) : (
                  <input
                    type={field.type}
                    value={form.credentials[field.key] || ''}
                    onChange={e => setCredField(field.key, e.target.value)}
                    placeholder={field.placeholder}
                    autoComplete="off"
                    style={{ display: 'block', width: '100%', marginTop: '4px', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px', boxSizing: 'border-box' }}
                  />
                )}
              </div>
            ))}
          </div>
          {saveError && (
            <div style={{ marginTop: '12px', padding: '8px 12px', background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: '6px', fontSize: '13px', color: '#991b1b' }}>
              <i className="fas fa-times-circle" style={{ marginRight: '6px' }} />{saveError}
            </div>
          )}
          <div style={{ marginTop: '12px', display: 'flex', gap: '10px' }}>
            <button onClick={save} disabled={saving || !form.displayName}
              style={{
                padding: '8px 20px', background: form.displayName ? '#2563eb' : '#e5e7eb',
                color: form.displayName ? '#fff' : '#9ca3af', border: 'none',
                borderRadius: '6px', cursor: form.displayName ? 'pointer' : 'default', fontSize: '13px', fontWeight: 600,
              }}>
              {saving ? t('ConnectorsTab.saving') : t('ConnectorsTab.saveConnector')}
            </button>
            <button onClick={() => { setShowForm(false); setSaveError(null); }}
              style={{ padding: '8px 16px', background: '#fff', border: '1px solid #d1d5db', borderRadius: '6px', cursor: 'pointer', fontSize: '13px' }}>
              {t('ConnectorsTab.cancel')}
            </button>
          </div>
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        {connectorsLoaded && connectors.map(c => {
          const job = syncJobs[c.id];
          const isRunning = job?.status === 'RUNNING' || job?.status === 'PENDING';
          return (
          <div key={c.id} style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px', overflow: 'hidden' }}>
            <div style={{ padding: '16px', display: 'flex', alignItems: 'center', gap: '16px' }}>
              <div style={{ width: '44px', height: '44px', borderRadius: '8px', background: '#f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <i className={`fab ${CONNECTOR_ICONS[c.connectorType] || 'fa-plug'}`} style={{ fontSize: '20px', color: '#374151' }} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: '14px', color: '#111827' }}>{c.displayName}</div>
                <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>
                  {c.connectorType.replace('_', ' ')}
                  {c.region && <span> · {c.region}</span>}
                  {c.accountId && <span> · {c.accountId}</span>}
                  {c.connectionSource === 'STORED_CREDENTIALS' && (
                    <span> · <i className="fas fa-lock" style={{ fontSize: '10px', marginRight: '2px' }} />{t('ConnectorsTab.credentialsStored')}</span>
                  )}
                  {c.connectionSource === 'ORG_CONNECTION' && (
                    <span style={{ color: '#2563eb' }}> · <i className="fas fa-link" style={{ fontSize: '10px', marginRight: '2px' }} />{t('ConnectorsTab.usesOrgConnection')}</span>
                  )}
                  {(!c.connectionSource || c.connectionSource === 'NONE') && (
                    <span style={{ color: '#d97706', fontWeight: 600 }}> · <i className="fas fa-exclamation-triangle" style={{ marginRight: '2px' }} />{t('ConnectorsTab.noCredentials')}</span>
                  )}
                </div>
                {c.lastSyncAt && (
                  <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '2px' }}>
                    {t('ConnectorsTab.lastSync', { time: new Date(c.lastSyncAt).toLocaleString() })}
                  </div>
                )}
                {c.lastError && !isRunning && (
                  <div style={{ fontSize: '11px', color: '#dc2626', marginTop: '2px' }}>
                    <i className="fas fa-exclamation-circle" style={{ marginRight: '3px' }} />{c.lastError.substring(0, 120)}
                  </div>
                )}
                {testResults[c.id] && (
                  <div style={{ fontSize: '12px', marginTop: '4px', color: testResults[c.id].success ? '#065f46' : '#991b1b' }}>
                    <i className={`fas ${testResults[c.id].success ? 'fa-check-circle' : 'fa-times-circle'}`} style={{ marginRight: '4px' }} />
                    {testResults[c.id].message}
                  </div>
                )}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'flex-end', flexShrink: 0 }}>
                <StatusBadge status={c.status} />
                <div style={{ display: 'flex', gap: '8px', marginTop: '8px', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                  <button onClick={() => testConn(c.id)} disabled={testingIds.has(c.id) || isRunning}
                    style={{ padding: '5px 12px', border: '1px solid #d1d5db', background: '#fff', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', color: '#374151' }}>
                    {testingIds.has(c.id) ? <i className="fas fa-spinner fa-spin" /> : t('ConnectorsTab.test')}
                  </button>
                  <button
                    onClick={() => { setEditingId(editingId === c.id ? null : c.id); setEditForm({ displayName: c.displayName, region: c.region || '', credentials: {} }); }}
                    style={{ padding: '5px 12px', border: '1px solid #d1d5db', background: '#fff', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', color: '#374151' }}>
                    <i className="fas fa-edit" style={{ marginRight: '4px' }} />{t('ConnectorsTab.edit')}
                  </button>
                  <button onClick={() => syncConn(c.id)} disabled={isRunning || !c.hasCredentials}
                    title={!c.hasCredentials ? t('ConnectorsTab.addCredentialsBeforeSyncing') : undefined}
                    style={{ padding: '5px 14px', background: (isRunning || !c.hasCredentials) ? '#e5e7eb' : '#2563eb', color: (isRunning || !c.hasCredentials) ? '#9ca3af' : '#fff', border: 'none', borderRadius: '6px', cursor: (isRunning || !c.hasCredentials) ? 'default' : 'pointer', fontSize: '12px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '5px' }}>
                    {isRunning
                      ? <><i className="fas fa-spinner fa-spin" />{t('ConnectorsTab.syncing')}</>
                      : <>{t('ConnectorsTab.syncNow')} <span style={{ fontSize: '9px', background: 'rgba(255,255,255,0.25)', padding: '1px 4px', borderRadius: '3px' }}>{t('ConnectorsTab.creditsBadge', { count: 5 })}</span></>}
                  </button>
                  <button onClick={() => deleteConnector(c.id)}
                    style={{ padding: '5px 10px', background: '#fee2e2', color: '#991b1b', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px' }}>
                    <i className="fas fa-trash" />
                  </button>
                </div>
              </div>
            </div>

            {/* Sync progress / result banner */}
            {job && (
              <div style={{
                padding: '10px 16px', borderTop: '1px solid #f3f4f6',
                background: job.status === 'COMPLETED' ? '#f0fdf4' : job.status === 'FAILED' ? '#fef2f2' : '#eff6ff',
                fontSize: '13px',
                color: job.status === 'COMPLETED' ? '#15803d' : job.status === 'FAILED' ? '#dc2626' : '#1d4ed8',
              }}>
                {(job.status === 'PENDING' || job.status === 'RUNNING') && (
                  <><i className="fas fa-spinner fa-spin" style={{ marginRight: '8px' }} />
                  {t('ConnectorsTab.scanningInfrastructure')}</>
                )}
                {job.status === 'COMPLETED' && job.result && (
                  <><i className="fas fa-check-circle" style={{ marginRight: '8px' }} />
                  {t('ConnectorsTab.scanCompletePrefix')} <strong>{job.result.assetsDiscovered ?? 0}</strong> {t('ConnectorsTab.scanAssetsDiscovered')}{' '}
                  <strong>{job.result.edgesMapped ?? 0}</strong> {t('ConnectorsTab.scanEdgesMapped')}{' '}
                  <strong>{job.result.findingsGenerated ?? 0}</strong> {t('ConnectorsTab.scanFindingsGenerated')}</>
                )}
                {job.status === 'FAILED' && (
                  <><i className="fas fa-times-circle" style={{ marginRight: '8px' }} />
                  {t('ConnectorsTab.syncFailed', { error: job.result?.errorMessage || t('ConnectorsTab.unknownError') })}</>
                )}
              </div>
            )}

            {/* Inline edit form */}
            {editingId === c.id && (
              <div style={{ padding: '16px', borderTop: '1px solid #f3f4f6', background: '#f9fafb' }}>
                <div style={{ fontSize: '12px', fontWeight: 600, color: '#374151', marginBottom: '12px' }}>{t('ConnectorsTab.updateConnector')}</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                  <div>
                    <label style={{ fontSize: '11px', fontWeight: 500, color: '#6b7280' }}>{t('ConnectorsTab.displayName')}</label>
                    <input value={editForm.displayName || ''} onChange={e => setEditForm(p => ({ ...p, displayName: e.target.value }))}
                      style={{ display: 'block', width: '100%', marginTop: '3px', padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px', boxSizing: 'border-box' }} />
                  </div>
                  {c.connectorType === 'AWS' && (
                    <div>
                      <label style={{ fontSize: '11px', fontWeight: 500, color: '#6b7280' }}>{t('ConnectorsTab.region')}</label>
                      <input value={editForm.region || ''} onChange={e => setEditForm(p => ({ ...p, region: e.target.value }))}
                        style={{ display: 'block', width: '100%', marginTop: '3px', padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px', boxSizing: 'border-box' }} />
                    </div>
                  )}
                  {c.connectionSource !== 'ORG_CONNECTION' && (CONNECTOR_FIELDS[c.connectorType] || []).map(field => (
                    <div key={field.key} style={field.type === 'textarea' ? { gridColumn: '1 / -1' } : {}}>
                      <label style={{ fontSize: '11px', fontWeight: 500, color: '#6b7280' }}>
                        {field.label} <span style={{ color: '#9ca3af' }}>{t('ConnectorsTab.leaveBlankToKeep')}</span>
                      </label>
                      {field.type === 'textarea' ? (
                        <textarea
                          value={editForm.credentials?.[field.key] || ''}
                          onChange={e => setEditForm(p => ({ ...p, credentials: { ...p.credentials, [field.key]: e.target.value } }))}
                          placeholder={field.placeholder}
                          rows={5}
                          style={{ display: 'block', width: '100%', marginTop: '3px', padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '11px', fontFamily: 'monospace', boxSizing: 'border-box', resize: 'vertical' }}
                        />
                      ) : (
                        <input type={field.type} value={editForm.credentials?.[field.key] || ''}
                          onChange={e => setEditForm(p => ({ ...p, credentials: { ...p.credentials, [field.key]: e.target.value } }))}
                          placeholder={field.placeholder}
                          autoComplete="off"
                          style={{ display: 'block', width: '100%', marginTop: '3px', padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px', boxSizing: 'border-box' }} />
                      )}
                    </div>
                  ))}
                </div>
                {editError && (
                  <div style={{ marginTop: '10px', fontSize: '12px', color: '#dc2626' }}>
                    <i className="fas fa-exclamation-circle" style={{ marginRight: '4px' }} />{editError}
                  </div>
                )}
                <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
                  <button onClick={() => saveEdit(c.id)} disabled={savingEdit}
                    style={{ padding: '6px 16px', background: savingEdit ? '#93c5fd' : '#2563eb', color: '#fff', border: 'none', borderRadius: '6px', cursor: savingEdit ? 'default' : 'pointer', fontSize: '12px', fontWeight: 600 }}>
                    {savingEdit ? t('ConnectorsTab.saving') : t('ConnectorsTab.save')}
                  </button>
                  <button onClick={() => { setEditingId(null); setEditError(null); }}
                    style={{ padding: '6px 12px', background: '#f3f4f6', color: '#374151', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px' }}>{t('ConnectorsTab.cancel')}</button>
                </div>
              </div>
            )}
          </div>
          );
        })}
        {!connectorsLoaded && (
          <div style={{ textAlign: 'center', padding: '48px', color: '#9ca3af' }}>
            <i className="fas fa-spinner fa-spin" style={{ fontSize: '20px' }} />
          </div>
        )}
        {connectorsLoaded && connectors.length === 0 && (
          <div style={{ textAlign: 'center', padding: '48px', color: '#9ca3af', border: '1px solid #e5e7eb', borderRadius: '8px' }}>
            <i className="fas fa-plug" style={{ fontSize: '32px', marginBottom: '12px', display: 'block' }} />
            <p style={{ margin: 0, fontWeight: 500 }}>{t('ConnectorsTab.noConnectorsConfigured')}</p>
            <p style={{ margin: '4px 0 0', fontSize: '13px' }}>{t('ConnectorsTab.noConnectorsHint')}</p>
          </div>
        )}
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  const styles = {
    ACTIVE:   { bg: '#d1fae5', color: '#065f46' },
    INACTIVE: { bg: '#f3f4f6', color: '#6b7280' },
    ERROR:    { bg: '#fee2e2', color: '#991b1b' },
  };
  const s = styles[status] || styles.INACTIVE;
  return (
    <span style={{ ...s, padding: '2px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: 600 }}>
      {status}
    </span>
  );
}
